import React from 'react';
import { getCategoryColor, getCategoryEmoji } from '../../utils/categories';
import { useFoodContext } from '../../context';

/**
 * Food Card Component
 * @param {Object} props - Component props
 * @param {Object} props.food - Food object
 * @param {boolean} props.showActions - Whether to show action buttons
 * @param {Function} props.onCustomAction - Custom action callback
 * @param {string} props.customActionLabel - Custom action button label
 */
const FoodCard = ({ 
  food, 
  showActions = true, 
  onCustomAction,
  customActionLabel = 'Action'
}) => {
  const { toggleFoodSelection, isSelectedFood } = useFoodContext();
  
  const isSelected = isSelectedFood(food.id);

  const handleToggleSelection = () => {
    toggleFoodSelection(food);
  };

  const handleCustomAction = () => {
    if (onCustomAction) {
      onCustomAction(food);
    }
  };

  return (
    <div className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all border border-gray-100">
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex-1">
          <h3 className="font-semibold text-lg text-gray-800 mb-1">
            {food.name}
          </h3>
          
          {food.brand && (
            <p className="text-sm text-gray-500 mb-2">{food.brand}</p>
          )}
          
          <div className="flex items-center gap-2 flex-wrap">
            {/* Badge de source */}
            {food.source === 'master' && (
              <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded font-medium">
                Référence
              </span>
            )}
            {food.source === 'manuel' && (
              <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded font-medium">
                Perso
              </span>
            )}
            {(food.source === 'off' || food.source === 'openfoodfacts') && (
              <span className="text-xs bg-orange-100 text-orange-800 px-2 py-1 rounded font-medium">
                Open Food Facts
              </span>
            )}
            {(food.source === 'ia' || food.source === 'ai') && (
              <span className="text-xs bg-purple-100 text-purple-800 px-2 py-1 rounded font-medium">
                Estimé par IA
              </span>
            )}

            {food.nutriscore && (
              <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded">
                Nutri-Score: {food.nutriscore.toUpperCase()}
              </span>
            )}

            {food.barcode && (
              <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded">
                {food.barcode}
              </span>
            )}
          </div>
        </div>
        
        <span className={`px-3 py-1 rounded-full text-sm font-medium border ${getCategoryColor(food.category)}`}>
          {getCategoryEmoji(food.category)} {food.category}
        </span>
      </div>
      
      {/* Nutrition Info */}
      <div className="space-y-3 mb-4">
        <div className="flex justify-between items-center">
          <span className="text-gray-600">Indice Glycémique</span>
          <span className="font-bold text-blue-600">{food.gi}</span>
        </div>
        
        <div className="flex justify-between items-center">
          <span className="text-gray-600">Glucides (g/100g)</span>
          <span className="font-bold text-green-600">{food.carbs}g</span>
        </div>
        
        <div className="flex justify-between items-center">
          <span className="text-gray-600">Charge Glycémique</span>
          <span className="font-bold text-purple-600">{food.gl}</span>
        </div>
      </div>

      {/* Actions */}
      {showActions && (
        <div className="space-y-2">
          <button
            onClick={handleToggleSelection}
            className={`w-full py-2 px-4 rounded-lg font-medium transition-all ${
              isSelected
                ? 'bg-blue-600 text-white hover:bg-blue-700'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            {isSelected ? 'Retirer du repas' : 'Ajouter au repas'}
          </button>
          
          {onCustomAction && (
            <button
              onClick={handleCustomAction}
              className="w-full py-2 px-4 rounded-lg font-medium bg-green-600 text-white hover:bg-green-700 transition-all"
            >
              {customActionLabel}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default FoodCard;